import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ActionMenu, Button, Input } from '@/presentation/components/common';
import { useCollectionsReport } from '@/presentation/hooks/useCollectionsReport';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { useToast } from '@/presentation/contexts/ToastContext';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import { CreateMpPreferenceAction } from '@/application/actions';
import type { CollectionsDebtorDTO } from '@/domain/repositories/IReportRepository';
import type { TenantGridDTO } from '@/domain/repositories/ITenantRepository';
import { RegisterPaymentModal } from '@/presentation/components/tenants/RegisterPaymentModal';
import { ViewTenantModal } from '@/presentation/components/tenants/ViewTenantModal';
import { ContactDebtorModal } from '@/presentation/components/collections/ContactDebtorModal';
import {
  CONTACT_STATUS_COLORS,
  fetchLatestContacts,
  type LastContactMap,
} from '@/presentation/components/collections/contactHistoryUtils';
import { usePlans } from '@/presentation/hooks/usePlans';

type AgingFilter = 'all' | '0-30' | '31-60' | '61-90' | '90+';
type SortField = 'nombreClinica' | 'totalAdeudado' | 'estadoPago' | 'daysOverdue' | 'periods';

function debtorKey(debtor: CollectionsDebtorDTO): string {
  return debtor.tenantId || (debtor.clinicaId != null ? `c:${debtor.clinicaId}` : `p:${debtor.panelPagoId}`);
}

function debtorToTenant(debtor: CollectionsDebtorDTO): TenantGridDTO {
  return {
    id: debtor.tenantId || debtor.clinicaId || 0,
    nombre: debtor.nombreClinica,
    emailAdministrador: '',
    activo: true,
    fechaActivoUTC: new Date().toISOString(),
    montoPeriodoPago: debtor.totalAdeudado,
    planAdminId: debtor.planAdminId ? Number(debtor.planAdminId) : undefined,
    estadoPagoDescripcion: debtor.estadoPago,
    fechaPeriodoPago: debtor.periodo,
  };
}

function agingBarClass(bucket: string): string {
  if (bucket === '0-30') return 'bg-yellow-500';
  if (bucket === '31-60') return 'bg-orange-500';
  if (bucket === '61-90') return 'bg-red-400';
  return 'bg-red-600';
}

function agingChipClass(bucket: string): string {
  if (bucket === '0-30') {
    return 'bg-yellow-50 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-200 dark:border-yellow-800';
  }
  if (bucket === '31-60') {
    return 'bg-orange-50 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-200 dark:border-orange-800';
  }
  if (bucket === '61-90') {
    return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-800';
  }
  return 'bg-red-100 text-red-800 border-red-300 dark:bg-red-950/50 dark:text-red-200 dark:border-red-700';
}

export const CollectionsPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { canOperateBilling } = useAuth();
  const { success, error: showError } = useToast();
  const { report, isLoading, error, refetch } = useCollectionsReport();
  const paymentWindowDay = report?.paymentWindowCloseDay ?? 10;
  const { plans } = usePlans();
  const [searchTerm, setSearchTerm] = useState('');
  const [agingFilter, setAgingFilter] = useState<AgingFilter>('all');
  const [payingId, setPayingId] = useState<number | null>(null);
  const [paymentTenant, setPaymentTenant] = useState<TenantGridDTO | null>(null);
  const [viewDebtor, setViewDebtor] = useState<CollectionsDebtorDTO | null>(null);
  const [contactDebtor, setContactDebtor] = useState<CollectionsDebtorDTO | null>(null);
  const [lastContacts, setLastContacts] = useState<LastContactMap>({});
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [agingOpen, setAgingOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [kpiOpen, setKpiOpen] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches : true
  );
  const [sortField, setSortField] = useState<SortField>('daysOverdue');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) setSearchTerm(q);
  }, [searchParams]);

  useEffect(() => {
    if (!report?.debtors?.length) {
      setLastContacts({});
      return;
    }
    const ids = report.debtors
      .map((d) => d.tenantId)
      .filter((id): id is string => Boolean(id));
    void fetchLatestContacts(ids).then(setLastContacts);
  }, [report]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection(field === 'nombreClinica' || field === 'estadoPago' ? 'asc' : 'desc');
    }
  };

  const sortIcon = (field: SortField) => {
    if (sortField !== field) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const filteredDebtors = useMemo(() => {
    if (!report) return [];
    const term = searchTerm.trim().toLowerCase();
    const rows = report.debtors.filter((d) => {
      if (agingFilter !== 'all' && d.agingBucket !== agingFilter) return false;
      if (!term) return true;
      return (
        d.nombreClinica.toLowerCase().includes(term) ||
        d.periodo.includes(term) ||
        d.estadoPago.toLowerCase().includes(term) ||
        (d.tenantId || '').toLowerCase().includes(term) ||
        d.periods.some(
          (p) =>
            p.periodo.includes(term) || p.estadoPago.toLowerCase().includes(term)
        )
      );
    });

    const dir = sortDirection === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'nombreClinica':
          cmp = a.nombreClinica.localeCompare(b.nombreClinica, 'es');
          break;
        case 'totalAdeudado':
          cmp = a.totalAdeudado - b.totalAdeudado;
          break;
        case 'estadoPago':
          cmp = a.estadoPago.localeCompare(b.estadoPago, 'es');
          break;
        case 'daysOverdue':
          cmp = a.daysOverdue - b.daysOverdue;
          break;
        case 'periods':
          cmp = a.periods.length - b.periods.length;
          break;
      }
      return cmp * dir;
    });

    return rows;
  }, [report, searchTerm, agingFilter, sortField, sortDirection]);

  const toggleExpanded = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handlePayMp = async (debtor: CollectionsDebtorDTO) => {
    if (!debtor.tenantId && debtor.clinicaId == null) {
      showError(t('pages.collections.missingTenant'));
      return;
    }
    if (debtor.totalAdeudado <= 0) {
      showError(t('pages.collections.invalidAmount'));
      return;
    }

    const [anio, mes] = debtor.periodo.split('-');
    const tenantRef = debtor.tenantId || String(debtor.clinicaId);
    setPayingId(debtor.panelPagoId);
    try {
      success(t('pages.collections.creatingPreference'));
      const action = container.get<CreateMpPreferenceAction>(TYPES.CreateMpPreferenceAction);
      const result = await action.execute({
        title: `Cobranza ${debtor.nombreClinica} (deuda total)`,
        unitPrice: debtor.totalAdeudado,
        quantity: 1,
        externalReference: `TENANT-${tenantRef}-${anio}-${mes}-${Date.now()}`,
      });
      success(t('pages.collections.redirectingToMP'));
      window.location.href = result.initPoint;
    } catch (err) {
      showError(err instanceof Error ? err.message : t('pages.collections.paymentError'));
    } finally {
      setPayingId(null);
    }
  };

  const openTenant = (debtor: CollectionsDebtorDTO) => {
    if (!debtor.tenantId && debtor.clinicaId == null) {
      showError(t('pages.collections.missingTenant'));
      return;
    }
    setViewDebtor(debtor);
  };

  if (isLoading && !report) {
    return (
      <div className="h-full flex items-center justify-center p-4 text-gray-600 dark:text-gray-400">
        {t('common.loading')}
      </div>
    );
  }

  const maxAgingAmount = Math.max(...(report?.aging.map((a) => a.amount) || [1]), 1);

  const clearFilters = () => {
    setSearchTerm('');
    setAgingFilter('all');
  };

  const getAgingFilterLabel = (value: AgingFilter): string => {
    if (value === 'all') return t('pages.collections.allBuckets');
    return t(`pages.collections.aging.${value}`);
  };

  const activeFilterChips = [
    ...(searchTerm.trim()
      ? [{
          key: 'search',
          label: `${t('common.search')}: ${searchTerm.trim()}`,
          onRemove: () => setSearchTerm(''),
        }]
      : []),
    ...(agingFilter !== 'all'
      ? [{
          key: 'aging',
          label: `${t('pages.collections.agingFilter')}: ${getAgingFilterLabel(agingFilter)}`,
          onRemove: () => setAgingFilter('all'),
        }]
      : []),
  ];

  const renderPeriodsDetail = (debtor: CollectionsDebtorDTO, compact = false) => (
    <div className={compact ? 'mt-2.5 border-t border-slate-100 pt-2.5 dark:border-gray-700/80' : ''}>
      {!compact && (
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {t('pages.collections.monthlyDetail')}
        </p>
      )}
      {compact && (
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {t('pages.collections.monthlyDetail')}
        </p>
      )}
      <div className={compact ? 'space-y-1.5' : 'overflow-x-auto'}>
        {compact ? (
          debtor.periods.map((period) => (
            <div
              key={period.panelPagoId}
              className="flex items-baseline justify-between gap-3 rounded-lg bg-slate-50 px-2.5 py-1.5 dark:bg-gray-900/50"
            >
              <div className="min-w-0">
                <p className="text-xs font-medium text-slate-800 dark:text-slate-200">{period.periodo}</p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  {period.estadoPago}
                  {' · '}
                  {t('pages.collections.daysOverdueCount', { count: period.daysOverdue })}
                </p>
              </div>
              <div className="text-right tabular-nums">
                <p className="text-xs font-semibold text-slate-900 dark:text-white">
                  $
                  {(period.deudaAcumulada > 0 ? period.deudaAcumulada : period.monto).toLocaleString()}
                </p>
                <p className="text-[10px] text-slate-500 dark:text-slate-400">
                  ${period.monto.toLocaleString()}
                </p>
              </div>
            </div>
          ))
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-xs uppercase text-gray-500">
                <th className="py-1.5 pr-3 text-left font-medium">{t('pages.collections.period')}</th>
                <th className="py-1.5 pr-3 text-right font-medium">{t('pages.collections.periodAmount')}</th>
                <th className="py-1.5 pr-3 text-right font-medium">{t('pages.collections.accumulatedDebt')}</th>
                <th className="py-1.5 pr-3 text-left font-medium">{t('pages.collections.status')}</th>
                <th className="py-1.5 text-right font-medium">{t('pages.collections.daysOverdue')}</th>
              </tr>
            </thead>
            <tbody>
              {debtor.periods.map((period) => (
                <tr
                  key={period.panelPagoId}
                  className="border-t border-slate-200/80 dark:border-gray-700/80"
                >
                  <td className="py-2 pr-3 text-gray-800 dark:text-gray-200">{period.periodo}</td>
                  <td className="py-2 pr-3 text-right tabular-nums text-gray-700 dark:text-gray-300">
                    ${period.monto.toLocaleString()}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums font-medium text-gray-900 dark:text-white">
                    $
                    {(period.deudaAcumulada > 0 ? period.deudaAcumulada : period.monto).toLocaleString()}
                  </td>
                  <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">{period.estadoPago}</td>
                  <td className="py-2 text-right text-gray-900 dark:text-white">{period.daysOverdue}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  const debtorOverflowItems = (debtor: CollectionsDebtorDTO) => {
    const items: {
      icon: string;
      label: string;
      onClick: () => void;
      disabled?: boolean;
    }[] = [
      {
        icon: '🏥',
        label: t('pages.collections.openTenant'),
        onClick: () => openTenant(debtor),
      },
    ];
    if (canOperateBilling) {
      items.push({
        icon: '💵',
        label: t('pages.collections.registerPayment'),
        onClick: () => setPaymentTenant(debtorToTenant(debtor)),
      });
    }
    return items;
  };

  return (
    <div className="panel-page h-full min-w-0 flex flex-col !pb-3 overflow-hidden">
      <div className="panel-page-header flex-shrink-0">
        <div className="min-w-0">
          <h1 className="panel-page-title">{t('pages.collections.title')}</h1>
          <p className="panel-page-subtitle hidden sm:block">
            {t('pages.collections.subtitle', { day: paymentWindowDay })}
          </p>
          <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400 sm:hidden">
            {t('pages.collections.asOf')}
            {': '}
            {report?.asOf ? new Date(report.asOf).toLocaleString() : '—'}
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={refetch} className="w-full sm:w-auto">
          {t('common.refresh')}
        </Button>
      </div>

      {error && (
        <div className="flex-shrink-0 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20 sm:p-4">
          <p className="text-sm text-red-800 dark:text-red-200">{error.message}</p>
        </div>
      )}

      <div className="flex-shrink-0 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setKpiOpen((v) => !v)}
            className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
              kpiOpen
                ? 'border-teal-500/50 bg-teal-50 text-teal-800 dark:border-teal-400/40 dark:bg-teal-900/30 dark:text-teal-200'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700'
            }`}
            aria-expanded={kpiOpen}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
              />
            </svg>
            {kpiOpen ? t('pages.collections.hideKpis') : t('pages.collections.showKpis')}
            <svg
              className={`h-3.5 w-3.5 transition-transform ${kpiOpen ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {!kpiOpen && (
            <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
              <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-red-500/40 bg-red-50 px-2 py-0.5 text-[11px] font-medium leading-4 text-red-700 dark:border-red-400/40 dark:bg-red-900/20 dark:text-red-200">
                <span className="truncate">
                  {t('pages.collections.totalOutstanding')}: $
                  {(report?.totalOutstanding ?? 0).toLocaleString()}
                </span>
              </span>
              <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-yellow-500/40 bg-yellow-50 px-2 py-0.5 text-[11px] font-medium leading-4 text-yellow-800 dark:border-yellow-400/40 dark:bg-yellow-900/20 dark:text-yellow-200">
                <span className="truncate">
                  {t('pages.collections.totalDebtors')}: {report?.totalDebtors ?? 0}
                </span>
              </span>
            </div>
          )}
        </div>

        {kpiOpen && (
          <div className="grid grid-cols-2 gap-2 sm:gap-2.5 md:grid-cols-3 md:gap-3 lg:gap-4">
            <div className="panel-stat !p-2.5 sm:!p-3.5 border-l-4 border-red-500 md:!p-4">
              <p className="panel-stat-label">{t('pages.collections.totalOutstanding')}</p>
              <p className="panel-stat-value !mt-0.5 text-base sm:text-lg md:text-2xl">
                ${(report?.totalOutstanding ?? 0).toLocaleString()}
              </p>
            </div>
            <div className="panel-stat !p-2.5 sm:!p-3.5 border-l-4 border-yellow-500 md:!p-4">
              <p className="panel-stat-label">{t('pages.collections.totalDebtors')}</p>
              <p className="panel-stat-value !mt-0.5 text-base sm:text-lg md:text-2xl">
                {report?.totalDebtors ?? 0}
              </p>
            </div>
            <div className="panel-stat hidden border-l-4 border-teal-500 md:block md:!p-4">
              <p className="panel-stat-label">{t('pages.collections.asOf')}</p>
              <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-white sm:mt-2">
                {report?.asOf ? new Date(report.asOf).toLocaleString() : '—'}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="panel-card flex-shrink-0">
        <button
          type="button"
          onClick={() => setAgingOpen((open) => !open)}
          className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left sm:px-3.5 sm:py-2.5"
          aria-expanded={agingOpen}
        >
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
            <span className="sm:hidden">{t('pages.collections.agingTitleShort')}</span>
            <span className="hidden sm:inline">{t('pages.collections.agingTitle', { day: paymentWindowDay })}</span>
          </h2>
          <span className="shrink-0 text-sm text-gray-500 dark:text-gray-400">
            {agingOpen ? '▾' : '▸'}
          </span>
        </button>
        {agingOpen && (
          <div className="max-h-44 space-y-2.5 overflow-y-auto px-3 pb-3 sm:max-h-48 sm:space-y-3 sm:px-3.5 sm:pb-3.5">
            {(report?.aging ?? []).map((bucket) => (
              <button
                key={bucket.bucket}
                type="button"
                onClick={() =>
                  setAgingFilter((prev) => (prev === bucket.bucket ? 'all' : bucket.bucket))
                }
                className={`w-full text-left transition ${
                  agingFilter === bucket.bucket ? 'opacity-100' : 'opacity-90'
                }`}
              >
                <div className="mb-1 flex justify-between text-xs sm:text-sm">
                  <span className="text-gray-600 dark:text-gray-300">
                    {t(`pages.collections.aging.${bucket.bucket}`)} · {bucket.count}
                  </span>
                  <span className="font-semibold tabular-nums text-gray-900 dark:text-white">
                    ${bucket.amount.toLocaleString()}
                  </span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-gray-200 dark:bg-gray-700 sm:h-2">
                  <div
                    className={`h-1.5 rounded-full sm:h-2 ${agingBarClass(bucket.bucket)}`}
                    style={{ width: `${Math.max(4, (bucket.amount / maxAgingAmount) * 100)}%` }}
                  />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Filtros colapsables */}
      <div className="flex-shrink-0 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
              filtersOpen || activeFilterChips.length > 0
                ? 'border-teal-500/50 bg-teal-50 text-teal-800 dark:border-teal-400/40 dark:bg-teal-900/30 dark:text-teal-200'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700'
            }`}
            aria-expanded={filtersOpen}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M6 10h12M9 16h6" />
            </svg>
            {filtersOpen ? t('pages.collections.hideFilters') : t('pages.collections.showFilters')}
            {activeFilterChips.length > 0 && (
              <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-teal-600 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-white">
                {activeFilterChips.length}
              </span>
            )}
            <svg
              className={`h-3.5 w-3.5 transition-transform ${filtersOpen ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {!filtersOpen && activeFilterChips.length > 0 && (
            <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
              {activeFilterChips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={chip.onRemove}
                  className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-teal-500/50 bg-teal-50 px-2 py-0.5 text-[11px] font-medium leading-4 text-teal-700 transition-colors hover:bg-teal-100 dark:border-teal-400/50 dark:bg-teal-900/20 dark:text-teal-200 dark:hover:bg-teal-900/40"
                  title={chip.label}
                >
                  <span className="truncate">{chip.label}</span>
                  <span className="text-sm leading-none" aria-hidden>×</span>
                </button>
              ))}
            </div>
          )}

          <p className="ml-auto hidden text-xs tabular-nums text-slate-500 dark:text-slate-400 md:block">
            {filteredDebtors.length}
            {' / '}
            {report?.totalDebtors ?? 0}
          </p>
        </div>

        {filtersOpen && (
          <div className="rounded-lg border border-slate-200 bg-white p-2 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
              <div className="grid flex-1 grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-[minmax(16rem,1.3fr)_minmax(10rem,0.7fr)_minmax(11rem,0.75fr)]">
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('pages.collections.searchPlaceholder')}
                  className="h-8 py-1 text-xs"
                  aria-label={t('common.search')}
                  title={t('common.search')}
                />
                <select
                  value={agingFilter}
                  onChange={(e) => setAgingFilter(e.target.value as AgingFilter)}
                  className="block h-8 w-full rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  aria-label={t('pages.collections.agingFilter')}
                  title={t('pages.collections.agingFilter')}
                >
                  <option value="all">{t('pages.collections.allBuckets')}</option>
                  <option value="0-30">{t('pages.collections.aging.0-30')}</option>
                  <option value="31-60">{t('pages.collections.aging.31-60')}</option>
                  <option value="61-90">{t('pages.collections.aging.61-90')}</option>
                  <option value="90+">{t('pages.collections.aging.90+')}</option>
                </select>
                <select
                  value={`${sortField}:${sortDirection}`}
                  onChange={(e) => {
                    const [field, dir] = e.target.value.split(':') as [SortField, 'asc' | 'desc'];
                    setSortField(field);
                    setSortDirection(dir);
                  }}
                  className="block h-8 w-full rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white md:hidden xl:block"
                  aria-label={t('pages.collections.sortBy')}
                  title={t('pages.collections.sortBy')}
                >
                  <option value="daysOverdue:desc">{t('pages.collections.daysOverdue')} ↓</option>
                  <option value="daysOverdue:asc">{t('pages.collections.daysOverdue')} ↑</option>
                  <option value="totalAdeudado:desc">{t('pages.collections.totalDebt')} ↓</option>
                  <option value="totalAdeudado:asc">{t('pages.collections.totalDebt')} ↑</option>
                  <option value="nombreClinica:asc">{t('pages.collections.clinic')} ↑</option>
                  <option value="nombreClinica:desc">{t('pages.collections.clinic')} ↓</option>
                  <option value="estadoPago:asc">{t('pages.collections.status')} ↑</option>
                  <option value="periods:desc">{t('pages.collections.periods')} ↓</option>
                </select>
              </div>

              <button
                type="button"
                onClick={clearFilters}
                disabled={activeFilterChips.length === 0}
                className="inline-flex h-8 w-full items-center justify-center rounded-md border border-slate-300 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:bg-gray-700 dark:text-slate-300 dark:hover:bg-gray-600 dark:hover:text-white lg:w-8"
                title={t('pages.collections.clearFilters')}
                aria-label={t('pages.collections.clearFilters')}
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m-8 0h10" />
                </svg>
                <span className="ml-2 text-sm lg:hidden">{t('pages.collections.clearFilters')}</span>
              </button>
            </div>

            {activeFilterChips.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {activeFilterChips.map((chip) => (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={chip.onRemove}
                    className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-teal-500/50 bg-teal-50 px-2 py-0.5 text-[11px] font-medium leading-4 text-teal-700 transition-colors hover:bg-teal-100 dark:border-teal-400/50 dark:bg-teal-900/20 dark:text-teal-200 dark:hover:bg-teal-900/40"
                    title={chip.label}
                  >
                    <span className="truncate">{chip.label}</span>
                    <span className="text-sm leading-none" aria-hidden>×</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lista: cards phone / tabla md+ */}
      <div className="min-h-0 min-w-0 flex-1">
        {/* Cards mobile */}
        <div className="panel-safe-bottom h-full space-y-2 overflow-y-auto md:hidden">
          {filteredDebtors.length === 0 ? (
            <div className="panel-card px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400">
              {t('pages.collections.noDebtors')}
            </div>
          ) : (
            filteredDebtors.map((debtor) => {
              const key = debtorKey(debtor);
              const expanded = expandedKeys.has(key);
              const initial = (debtor.nombreClinica.trim().charAt(0) || '?').toUpperCase();
              const last = debtor.tenantId ? lastContacts[debtor.tenantId] : undefined;

              return (
                <div key={key} className="panel-list-card px-3.5 py-3">
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100 text-sm font-bold text-slate-700 dark:bg-gray-700 dark:text-slate-200">
                      {initial}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p
                            className="truncate text-[15px] font-semibold leading-snug text-slate-900 dark:text-white"
                            title={debtor.nombreClinica}
                          >
                            {debtor.nombreClinica}
                          </p>
                          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                            {debtor.estadoPago}
                            {' · '}
                            {t('pages.collections.periodsCount', { count: debtor.periods.length })}
                          </p>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <p className="text-[15px] font-bold tabular-nums leading-snug text-red-600 dark:text-red-400">
                            ${debtor.totalAdeudado.toLocaleString()}
                          </p>
                          <span
                            className={`panel-chip mt-1 border ${agingChipClass(debtor.agingBucket)}`}
                          >
                            {t('pages.collections.daysOverdueCount', { count: debtor.daysOverdue })}
                          </span>
                        </div>
                      </div>

                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <span className="rounded-md bg-slate-50 px-1.5 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-gray-900/50 dark:text-slate-300">
                          {debtor.periodo}
                        </span>
                        {last ? (
                          <span
                            className={`panel-chip ${
                              CONTACT_STATUS_COLORS[last.deliveryStatus] ||
                              CONTACT_STATUS_COLORS.pending
                            }`}
                            title={`${t('pages.collections.contact.lastContact')}: ${last.channel} · ${last.recipient}`}
                          >
                            {t(`pages.collections.contact.status.${last.deliveryStatus}`, {
                              defaultValue: last.deliveryStatus,
                            })}
                            {' · '}
                            {last.channel === 'email' ? 'Email' : 'WA'}
                          </span>
                        ) : null}
                      </div>

                      <div className="mt-2.5 flex flex-wrap items-center justify-end gap-1 border-t border-slate-100 pt-2 dark:border-gray-700/80">
                        <button
                          type="button"
                          onClick={() => toggleExpanded(key)}
                          className="mr-auto inline-flex h-8 items-center rounded-full border border-slate-200 bg-white px-2.5 text-[11px] font-medium text-slate-600 shadow-sm transition hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:text-slate-300 dark:hover:bg-gray-700"
                          aria-expanded={expanded}
                        >
                          {expanded ? '▾' : '▸'} {t('pages.collections.monthlyDetail')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setContactDebtor(debtor)}
                          className="inline-flex h-8 items-center gap-1 rounded-full border border-teal-200 bg-teal-50 px-2.5 text-[11px] font-medium text-teal-800 shadow-sm transition hover:bg-teal-100 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-200 dark:hover:bg-teal-900/50"
                          title={t('pages.collections.contact.title')}
                        >
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          {t('pages.collections.contact.action')}
                        </button>
                        {canOperateBilling && (
                          <button
                            type="button"
                            disabled={payingId === debtor.panelPagoId}
                            onClick={() => void handlePayMp(debtor)}
                            className="inline-flex h-8 items-center rounded-full border border-slate-200 bg-white px-2.5 text-[11px] font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700"
                          >
                            {payingId === debtor.panelPagoId
                              ? '…'
                              : t('pages.collections.payWithMP')}
                          </button>
                        )}
                        <ActionMenu
                          triggerTitle={t('common.actions')}
                          items={debtorOverflowItems(debtor)}
                        />
                      </div>

                      {expanded && renderPeriodsDetail(debtor, true)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Tabla desktop / iPad+ */}
        <div className="panel-table-wrap hidden h-full md:block">
          <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-gray-700">
            <thead className="panel-thead sticky top-0 z-10 bg-slate-50 dark:bg-gray-900">
              <tr>
                <th className="w-8 bg-slate-50 px-3 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:bg-gray-900" />
                <th
                  className="cursor-pointer select-none bg-slate-50 px-3 py-3 text-left text-xs font-medium uppercase text-gray-500 hover:bg-slate-100 dark:bg-gray-900 dark:hover:bg-gray-800"
                  onClick={() => handleSort('nombreClinica')}
                >
                  {t('pages.collections.clinic')}
                  {sortIcon('nombreClinica')}
                </th>
                <th
                  className="cursor-pointer select-none bg-slate-50 px-3 py-3 text-right text-xs font-medium uppercase text-gray-500 hover:bg-slate-100 dark:bg-gray-900 dark:hover:bg-gray-800"
                  onClick={() => handleSort('totalAdeudado')}
                >
                  {t('pages.collections.totalDebt')}
                  {sortIcon('totalAdeudado')}
                </th>
                <th
                  className="cursor-pointer select-none bg-slate-50 px-3 py-3 text-left text-xs font-medium uppercase text-gray-500 hover:bg-slate-100 dark:bg-gray-900 dark:hover:bg-gray-800"
                  onClick={() => handleSort('estadoPago')}
                >
                  {t('pages.collections.status')}
                  {sortIcon('estadoPago')}
                </th>
                <th
                  className="cursor-pointer select-none bg-slate-50 px-3 py-3 text-right text-xs font-medium uppercase text-gray-500 hover:bg-slate-100 dark:bg-gray-900 dark:hover:bg-gray-800"
                  onClick={() => handleSort('daysOverdue')}
                >
                  {t('pages.collections.daysOverdue')}
                  {sortIcon('daysOverdue')}
                </th>
                <th
                  className="hidden cursor-pointer select-none bg-slate-50 px-3 py-3.5 text-left text-xs font-medium uppercase text-gray-500 hover:bg-slate-100 lg:table-cell dark:bg-gray-900 dark:hover:bg-gray-800"
                  onClick={() => handleSort('periods')}
                >
                  {t('pages.collections.periods')}
                  {sortIcon('periods')}
                </th>
                <th className="bg-slate-50 px-3 py-3.5 text-right text-xs font-medium uppercase text-gray-500 dark:bg-gray-900">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredDebtors.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                    {t('pages.collections.noDebtors')}
                  </td>
                </tr>
              ) : (
                filteredDebtors.map((debtor) => {
                  const key = debtorKey(debtor);
                  const expanded = expandedKeys.has(key);
                  return (
                    <React.Fragment key={key}>
                      <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                        <td className="px-2 py-3">
                          <button
                            type="button"
                            onClick={() => toggleExpanded(key)}
                            className="flex h-7 w-7 items-center justify-center rounded text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                            title={t('pages.collections.monthlyDetail')}
                            aria-expanded={expanded}
                          >
                            {expanded ? '▾' : '▸'}
                          </button>
                        </td>
                        <td className="px-3 py-3 font-medium text-gray-900 dark:text-white">
                          <div className="flex flex-col gap-1">
                            <span>{debtor.nombreClinica}</span>
                            {debtor.tenantId && lastContacts[debtor.tenantId] ? (
                              <span
                                className={`inline-flex w-fit rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                  CONTACT_STATUS_COLORS[
                                    lastContacts[debtor.tenantId].deliveryStatus
                                  ] || CONTACT_STATUS_COLORS.pending
                                }`}
                                title={`${t('pages.collections.contact.lastContact')}: ${
                                  lastContacts[debtor.tenantId].channel
                                } · ${lastContacts[debtor.tenantId].recipient}`}
                              >
                                {t(
                                  `pages.collections.contact.status.${lastContacts[debtor.tenantId].deliveryStatus}`,
                                  {
                                    defaultValue:
                                      lastContacts[debtor.tenantId].deliveryStatus,
                                  }
                                )}
                                {' · '}
                                {lastContacts[debtor.tenantId].channel === 'email'
                                  ? 'Email'
                                  : 'WA'}
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right font-semibold tabular-nums text-red-600 dark:text-red-400">
                          ${debtor.totalAdeudado.toLocaleString()}
                        </td>
                        <td className="px-3 py-3 text-gray-600 dark:text-gray-300">
                          {debtor.estadoPago}
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums text-gray-900 dark:text-white">
                          {debtor.daysOverdue}
                        </td>
                        <td className="hidden px-3 py-3.5 text-gray-600 dark:text-gray-300 lg:table-cell">
                          {t('pages.collections.periodsCount', {
                            count: debtor.periods.length,
                          })}
                        </td>
                        <td className="px-3 py-3 text-right">
                          <div className="inline-flex flex-wrap justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setContactDebtor(debtor)}
                              title={t('pages.collections.contact.title')}
                              aria-label={t('pages.collections.contact.title')}
                            >
                              <span className="inline-flex items-center gap-1.5">
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={1.75}
                                  aria-hidden
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                  />
                                </svg>
                                <span className="hidden lg:inline">
                                  {t('pages.collections.contact.action')}
                                </span>
                              </span>
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openTenant(debtor)}>
                              {t('pages.collections.openTenant')}
                            </Button>
                            {canOperateBilling && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setPaymentTenant(debtorToTenant(debtor))}
                                >
                                  {t('pages.collections.registerPayment')}
                                </Button>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  disabled={payingId === debtor.panelPagoId}
                                  onClick={() => void handlePayMp(debtor)}
                                >
                                  {payingId === debtor.panelPagoId
                                    ? t('pages.collections.creatingPreference')
                                    : t('pages.collections.payWithMP')}
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="bg-slate-50/80 dark:bg-gray-900/40">
                          <td colSpan={7} className="px-3 py-3">
                            {renderPeriodsDetail(debtor, false)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {viewDebtor && (viewDebtor.tenantId || viewDebtor.clinicaId != null) && (
        <ViewTenantModal
          isOpen={!!viewDebtor}
          tenantId={viewDebtor.tenantId || viewDebtor.clinicaId!}
          debtor={viewDebtor}
          onClose={() => setViewDebtor(null)}
        />
      )}

      {contactDebtor && (
        <ContactDebtorModal
          isOpen={!!contactDebtor}
          debtor={{
            tenantId: contactDebtor.tenantId,
            clinicaId: contactDebtor.clinicaId,
            nombreClinica: contactDebtor.nombreClinica,
            nombreContacto: contactDebtor.nombreDueno,
            planName:
              plans.find((p) => p.id === Number(contactDebtor.planAdminId))?.nombre ?? null,
            emailAdministrador: contactDebtor.emailAdministrador,
            telefonoContacto: contactDebtor.telefonoContacto,
            periodo: contactDebtor.periodo,
            totalAdeudado: contactDebtor.totalAdeudado,
          }}
          onClose={() => setContactDebtor(null)}
          onContactSaved={() => {
            refetch();
            if (contactDebtor?.tenantId) {
              void fetchLatestContacts([contactDebtor.tenantId]).then((map) =>
                setLastContacts((prev) => ({ ...prev, ...map }))
              );
            }
          }}
        />
      )}

      {paymentTenant && (
        <RegisterPaymentModal
          key={String(paymentTenant.id)}
          isOpen={!!paymentTenant}
          tenant={paymentTenant}
          onClose={() => setPaymentTenant(null)}
          onSuccess={() => {
            setPaymentTenant(null);
            refetch();
          }}
        />
      )}
    </div>
  );
};
